import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';
import Button from '../../../components/ui/Button';

const InfluencerTable = ({ influencers }) => {
  const [sortField, setSortField] = useState('score');
  const [sortDirection, setSortDirection] = useState('desc');

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('desc');
    }
  };

  const sortedInfluencers = [...influencers]?.sort((a, b) => {
    const aValue = a?.[sortField];
    const bValue = b?.[sortField];
    const modifier = sortDirection === 'asc' ? 1 : -1;
    return aValue > bValue ? modifier : -modifier;
  });

  const SortIcon = ({ field }) => {
    if (sortField !== field) return <Icon name="ChevronsUpDown" size={14} className="text-muted-foreground" />;
    return <Icon name={sortDirection === 'asc' ? 'ChevronUp' : 'ChevronDown'} size={14} className="text-primary" />;
  };

  return (
    <div className="bg-card rounded-xl border border-border overflow-hidden">
      <div className="p-4 md:p-6 border-b border-border">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h3 className="text-lg md:text-xl font-semibold text-foreground mb-1">
              Top Influencers
            </h3>
            <p className="text-sm text-muted-foreground">
              Highest performing participants across campaigns
            </p>
          </div>
          <Button variant="outline" size="sm" iconName="Download">
            Export Data
          </Button>
        </div>
      </div>
      <div className="overflow-x-auto scrollbar-custom">
        <table className="w-full">
          <thead className="bg-muted/50">
            <tr>
              <th className="px-4 md:px-6 py-3 text-left">
                <button
                  onClick={() => handleSort('name')}
                  className="flex items-center gap-2 text-xs font-semibold text-muted-foreground hover:text-foreground transition-colors"
                >
                  Influencer
                  <SortIcon field="name" />
                </button>
              </th>
              <th className="px-4 md:px-6 py-3 text-left">
                <button
                  onClick={() => handleSort('score')}
                  className="flex items-center gap-2 text-xs font-semibold text-muted-foreground hover:text-foreground transition-colors"
                >
                  Score
                  <SortIcon field="score" />
                </button>
              </th>
              <th className="px-4 md:px-6 py-3 text-left">
                <button
                  onClick={() => handleSort('followers')}
                  className="flex items-center gap-2 text-xs font-semibold text-muted-foreground hover:text-foreground transition-colors"
                >
                  Followers
                  <SortIcon field="followers" />
                </button>
              </th>
              <th className="px-4 md:px-6 py-3 text-left">
                <button
                  onClick={() => handleSort('engagement')}
                  className="flex items-center gap-2 text-xs font-semibold text-muted-foreground hover:text-foreground transition-colors"
                >
                  Engagement
                  <SortIcon field="engagement" />
                </button>
              </th>
              <th className="px-4 md:px-6 py-3 text-left">
                <button
                  onClick={() => handleSort('conversions')}
                  className="flex items-center gap-2 text-xs font-semibold text-muted-foreground hover:text-foreground transition-colors"
                >
                  Conversions
                  <SortIcon field="conversions" />
                </button>
              </th>
              <th className="px-4 md:px-6 py-3 text-right text-xs font-semibold text-muted-foreground">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {sortedInfluencers?.map((influencer) => (
              <tr key={influencer?.id} className="hover:bg-muted/30 transition-colors">
                <td className="px-4 md:px-6 py-4">
                  <div className="flex items-center gap-3 min-w-[200px]">
                    <div className="w-10 h-10 rounded-full overflow-hidden flex-shrink-0">
                      <Image
                        src={influencer?.avatar}
                        alt={influencer?.avatarAlt}
                        className="w-full h-full object-cover"
                      />
                    </div>
                    <div className="min-w-0">
                      <p className="text-sm font-medium text-foreground truncate">
                        {influencer?.name}
                      </p>
                      <p className="text-xs text-muted-foreground truncate">
                        @{influencer?.handle}
                      </p>
                    </div>
                  </div>
                </td>
                <td className="px-4 md:px-6 py-4">
                  <div className="flex items-center gap-2">
                    <div className="w-12 h-12 rounded-lg bg-gradient-to-br from-primary to-secondary flex items-center justify-center">
                      <span className="text-sm font-bold text-white data-text">
                        {influencer?.score}
                      </span>
                    </div>
                  </div>
                </td>
                <td className="px-4 md:px-6 py-4">
                  <span className="text-sm text-foreground data-text whitespace-nowrap">
                    {influencer?.followers?.toLocaleString()}
                  </span>
                </td>
                <td className="px-4 md:px-6 py-4">
                  <span className="text-sm font-medium text-success data-text whitespace-nowrap">
                    {influencer?.engagement}%
                  </span>
                </td>
                <td className="px-4 md:px-6 py-4">
                  <span className="text-sm text-foreground data-text whitespace-nowrap">
                    {influencer?.conversions}
                  </span>
                </td>
                <td className="px-4 md:px-6 py-4">
                  <div className="flex items-center justify-end gap-2">
                    <Button variant="ghost" size="sm" iconName="Eye">
                      View
                    </Button>
                    <Button variant="ghost" size="sm" iconName="MessageSquare">
                      Contact
                    </Button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default InfluencerTable;